import * as _ from "lodash";
import { IBusinessObject } from "../PA.Core/PA.Core.IF.BO";
import { IMessageProvider, IProgressProvider, MessageType } from "../PA.Core/PA.Core.IF.Basic";
import { PAdbContext } from "../PA.Core/PAdbContext";
import { IdentityProvider } from "../PA.Core/Basic/Identity";
import { MessageProvider } from "../PA.Core/Basic/Messages";
import { ProgressProvider } from "../PA.Core/Basic/Progress";
import { WB_SimulationInputRow } from "../PA.Core/PAdbSchemaRows";

import {
    esWB_CBO_Input_Relationship,
    esWB_ScnPlan_InputR,
    esWB_Simulation_Input,
    esWB_Simulation_Input_Relationship,
} from "../PA.Core/PAdbSchema";
import { ESRecord } from "../PA.Core/lib/ESRecord";
import { ESDataStore } from "../PA.Core/lib/ESDataStore";
import { CreateOptions, ESRecordResolver } from "../PA.Core/lib/ESRecordResolver";
import { ASync } from "../PA.Core/Basic/ASync";
import { PAEntity } from "../PA.Core/PAEntity";
import { PAEnvironment } from "../PA.Core/PAEnvironment";

export class SimulationInputBO implements IBusinessObject {
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_SimulationInput: WB_SimulationInputRow;
    _bIdEditable: boolean;

    constructor(options?: any) {
        this.CreateOptions = options || {};

        if ("dbContext" in this.CreateOptions) this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({ id: 0, name: "New Simulation Input" });

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider("Loading", this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_SimulationInput = null;
        this._bIdEditable = true;
    }

    get WB_SimulationInput(): WB_SimulationInputRow {
        return this.drWB_SimulationInput;
    }

    get bIdEditable(): boolean {
        return this._bIdEditable;
    }

    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating Simulation Input");
        this._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {
            self.LoadOptions = options || {};
            if ("dbContext" in self.LoadOptions) self.dbContext = self.LoadOptions.dbContext;
            else self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                let sError = "Unable to determine dbContext for New Simulation Input.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            // Create record using dbContext
            this.dbContext
                .createRecordWithKey(esWB_Simulation_Input)
                .then((newSimulationInputRow: WB_SimulationInputRow) => {
                    newSimulationInputRow.setDefaults();
                    self.drWB_SimulationInput = newSimulationInputRow;
                    self._bIdEditable = true;
                    self.dbContext.add(esWB_Simulation_Input, newSimulationInputRow);
                    self.Identity.setAll(
                        self.WB_SimulationInput.cif.WB_SIMULATION_INPUT_ID,
                        self.WB_SimulationInput.cif.SIMULATION_INPUT_ID,
                        self.WB_SimulationInput.cif.SIMULATION_INPUT_NAME,
                    );
                    self.LoadProgressProvider.setName(
                        "Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")",
                    );
                    self.Identity.listenToRecordUpdates(
                        self.WB_SimulationInput,
                        "WB_SIMULATION_INPUT_ID",
                        "SIMULATION_INPUT_ID",
                        "SIMULATION_INPUT_NAME",
                    );

                    self.LoadProgressProvider.finish();
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    resolve(self);
                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err);
                    error(err);
                });
        });

        return this._OnLoadComplete;
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {
        const self = this;

        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start("Loading (" + self.Identity.id_ui + ")");

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {
            if (self.bIsLoaded) {
                let sError = "Simulation Input already loaded.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            self.LoadOptions = options || {};

            if ("dbContext" in self.LoadOptions) self.dbContext = self.LoadOptions.dbContext;
            else if (self.dbContext == null) self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                let sError = "Unable to determine dbContext for Simulation Input.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            if ("RecordResolver" in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = ASync.resolve();
            } else {
                recordResolver = SimulationInputBO.createRecordResolver({ dbContext: self.dbContext });
                recordResolverLoadPromise = SimulationInputBO.setupRecordResolver(recordResolver, [anId]);
            }

            recordResolverLoadPromise
                .then(function () {
                    let newSimulationInputRow: any = recordResolver.get(esWB_Simulation_Input, anId);

                    if (_.isEmpty(newSimulationInputRow)) {
                        self._OnLoadComplete = null;
                        self.LoadProgressProvider.exception("Id not found.");
                        resolve(self);
                        return;
                    }

                    self.drWB_SimulationInput = newSimulationInputRow;
                    self.Identity.setAll(
                        self.WB_SimulationInput.cif.WB_SIMULATION_INPUT_ID,
                        self.WB_SimulationInput.cif.SIMULATION_INPUT_ID,
                        self.WB_SimulationInput.cif.SIMULATION_INPUT_NAME,
                    );
                    self.LoadProgressProvider.setName(
                        "Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")",
                    );
                    self.Identity.listenToRecordUpdates(
                        self.WB_SimulationInput,
                        "WB_SIMULATION_INPUT_ID",
                        "SIMULATION_INPUT_ID",
                        "SIMULATION_INPUT_NAME",
                    );
                })
                .then((newSimulationInputRow: any) => {
                    let aPAEntity = new PAEntity();
                    return aPAEntity.checkCustomColumnExists(
                        "SimulationInput",
                        self.drWB_SimulationInput.cif.WB_SIMULATION_INPUT_ID,
                        "/AZR/WB_SIMULATION_INPUTR",
                        self.drWB_SimulationInput.cif.SIMULATION_INPUT_ID,
                    );
                })
                .then((bExist: boolean) => {
                    self._bIdEditable = !bExist;
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.finish();
                    resolve(self);
                })
                .catch((err: any) => {
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(err);
                    error(err);
                });
        });

        return this._OnLoadComplete;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    async Validate(options?: any): Promise<IBusinessObject> {
        let self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start(
            "Validating " +
                self.drWB_SimulationInput.cif.SIMULATION_INPUT_NAME +
                "(" +
                self.drWB_SimulationInput.cif.SIMULATION_INPUT_ID +
                ")",
        );

        // Does no validations yet
        self.ValidateOptions = options || {};

        if (self.dbContext == undefined) {
            let sError = "Unable to determine dbContext to Validate Simulation Input.";
            self.ValidateProgressProvider.exception(sError);
            self._bIsValidated = false;
            return self;
        }

        self.WB_SimulationInput.cif.SIMULATION_INPUT_ID = _.trim(self.WB_SimulationInput.cif.SIMULATION_INPUT_ID);

        let simulationInputId = self.WB_SimulationInput.cif.SIMULATION_INPUT_ID;

        if (_.isEmpty(simulationInputId)) {
            self.ValidateMessageProvider.addMessage({
                type: MessageType.Error,
                code: "Empty_Id",
                title: "Empty Simulation Input Id",
                description: "Simulation Input Id must not be empty: " + simulationInputId,
            });
        }

        let modelIdPattern = /[A-Z]+[A-Z_0-9]*/g;
        let matches = simulationInputId.match(modelIdPattern);
        let firstMatch = matches == null ? undefined : matches[0];

        let bValidColumnNameId = matches?.length == 1 && simulationInputId == firstMatch;

        if (!bValidColumnNameId) {
            self.ValidateMessageProvider.addMessage({
                type: MessageType.Error,
                code: "Invalid_Id",
                title: "Invalid Simulation Input Id",
                description:
                    "Simulation Input Id must contain uppercase/ underscores letters only, got: " + simulationInputId,
            });
        }

        self._bIsValidated = self.ValidateMessageProvider.countMessages(MessageType.Error) == 0;

        self.ValidateProgressProvider.finish();
        return self;
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options);
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options);
    }

    private async SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        let aPAEntity = new PAEntity();

        this.SaveProgressProvider.start(
            "Saving " +
                self.WB_SimulationInput.cif.SIMULATION_INPUT_NAME +
                "(" +
                self.WB_SimulationInput.cif.SIMULATION_INPUT_ID +
                ")",
        );

        // Doesn't need to do anything yet
        // All the records are in the store, since they were added through find or createNewRecord.
        // We could search the tree if there is any record where store isn't set.

        if (_.isEmpty(self.dbContext)) {
            self._bIsSaved = false;
            let sError = "Unable to determine dbContext to Save Simulation Input";
            self.SaveProgressProvider.exception(sError);
            throw sError;
        }

        self.SaveOptions = options || {};

        let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>;
        if (bBatch) {
            saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
        } else {
            saveFunction = self.dbContext.Save.bind(self.dbContext);
        }

        await self.Validate({});

        if (!self.bIsValidated) {
            self._bIsSaved = false;
            self.SaveProgressProvider.finish();
            resolve(self);
            return;
        }

        await saveFunction(esWB_Simulation_Input, self.drWB_SimulationInput, {});

        await aPAEntity.createOrUpdateCustomColumn(
            esWB_Simulation_Input_Relationship,
            self.drWB_SimulationInput.cif.WB_SIMULATION_INPUT_ID,
            "/AZR/WB_SIMULATION_INPUTR",
            self.drWB_SimulationInput.cif.SIMULATION_INPUT_ID,
            {
                APP_ID: self.drWB_SimulationInput.cif.SIMULATION_INPUT_ID,
                MAPPER_ID: esWB_Simulation_Input_Relationship,
                SERVER_MODEL_ID: esWB_Simulation_Input_Relationship,
                COLUMN_DATATYPE: "DECIMAL",
                COLUMN_LENGTH: 15,
                COLUMN_PRECISION: 6,
                NOTES: self.WB_SimulationInput.cif.COMMENTS || "",
            },
        );

        await aPAEntity.createOrUpdateCustomColumn(
            esWB_CBO_Input_Relationship,
            self.drWB_SimulationInput.cif.WB_SIMULATION_INPUT_ID,
            "/AZR/WB_CBO_INPUTR",
            self.drWB_SimulationInput.cif.SIMULATION_INPUT_ID,
            {
                APP_ID: self.drWB_SimulationInput.cif.SIMULATION_INPUT_ID,
                MAPPER_ID: esWB_CBO_Input_Relationship,
                SERVER_MODEL_ID: esWB_CBO_Input_Relationship,
                COLUMN_DATATYPE: "DECIMAL",
                COLUMN_LENGTH: 15,
                COLUMN_PRECISION: 6,
                NOTES: self.WB_SimulationInput.cif.COMMENTS || "",
            },
        );

        await aPAEntity.createOrUpdateCustomColumn(
            esWB_ScnPlan_InputR,
            self.drWB_SimulationInput.cif.WB_SIMULATION_INPUT_ID,
            "/AZR/WB_SCNPLAN_INPUTR",
            self.drWB_SimulationInput.cif.SIMULATION_INPUT_ID,
            {
                APP_ID: self.drWB_SimulationInput.cif.SIMULATION_INPUT_ID,
                MAPPER_ID: esWB_ScnPlan_InputR,
                SERVER_MODEL_ID: esWB_ScnPlan_InputR,
                COLUMN_DATATYPE: "DECIMAL",
                COLUMN_LENGTH: 15,
                COLUMN_PRECISION: 6,
                NOTES: self.WB_SimulationInput.cif.COMMENTS || "",
            },
        );

        if (bBatch) {
            self.dbContext.addBatchCompleteListener(() => {
                self._bIsSaved = true;
            });
        } else {
            self._bIsSaved = true;
        }
        self._bIdEditable = false;
        self.SaveProgressProvider.finish();
        return self;
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    //Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options);
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options);
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start(
            "Deleting " +
                self.drWB_SimulationInput.cif.SIMULATION_INPUT_NAME +
                "(" +
                self.drWB_SimulationInput.cif.SIMULATION_INPUT_ID +
                ")",
        );

        return new Promise<IBusinessObject>((resolve, error) => {
            // 1. Validate if we can delete the Model

            // 2. If Valid, mark all the records for deletion.
            self.drWB_SimulationInput.markDeleted();

            if (bSaveNow) {
                self.SaveUsing(false)
                    .then((ignore) => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);
                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err);
                        resolve(self);
                    });
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        let opts = boLoadOptions || {};
        let dbContext: PAdbContext;

        if ("dbContext" in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }

        const simulation_input_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {},
        };

        const aRecResolver = new ESRecordResolver(dbContext, simulation_input_options);

        return aRecResolver;
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {
        return new Promise<void>((resolve, error) => {
            recordResolver
                .findAllWithRelationships(esWB_Simulation_Input, ids)
                .then((result) => {
                    resolve();
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    static concurrentLoadCountHint(): number {
        return 10;
    }

    //</editor-fold>
}

PAEnvironment.registerBusinessObjectClass(SimulationInputBO, "SimulationInputBO", "Simulation Input");
